import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { addCustomListItems } from '../services/addCustomListItems';
import { createCustomList } from '../services/createCustomList';
import { deleteCustomList } from '../services/deleteCustomList';
import { fetchCustomListDetail } from '../services/fetchCustomListDetail';
import { fetchCustomLists } from '../services/fetchCustomLists';
import { removeCustomListItems } from '../services/removeCustomListItems';
import { renameCustomList } from '../services/renameCustomList';
import { updateCustomListItem } from '../services/updateCustomListItem';
import { CustomListSchema } from '../types/CustomListSchema';

const initialState: CustomListSchema = {
    lists: [],
    isListsLoading: false,
    detail: null,
    isDetailLoading: false,
    isManaging: false,
};

const updatesDetail = (updateDetail?: boolean) => updateDetail !== false;

const resetListsOnPropertyChange = (
    state: CustomListSchema,
    propertyId: string,
) => {
    if (state.listsPropertyId === propertyId) {
        return;
    }

    state.lists = [];
    state.listsPropertyId = propertyId;

    const detailBelongsToProperty =
        state.detail != null && state.detail.propertyId === propertyId;

    if (!detailBelongsToProperty) {
        state.detail = null;
        state.selectedListId = undefined;
        state.detailRequestId = undefined;
        state.detailError = undefined;
        state.isDetailLoading = false;
    }
};

const upsertListFromDetail = (
    state: CustomListSchema,
    detail: NonNullable<CustomListSchema['detail']>,
) => {
    const list = {
        id: detail.id,
        propertyId: detail.propertyId,
        name: detail.name,
        itemCount: detail.items?.length ?? 0,
        createdAt: detail.createdAt,
        updatedAt: detail.updatedAt,
    };
    const index = state.lists.findIndex((item) => item.id === detail.id);
    if (index >= 0) {
        state.lists[index] = list;
    } else {
        state.lists.push(list);
    }
};

export const customListSlice = createSlice({
    name: 'customList',
    initialState,
    reducers: {
        setSelectedListId: (
            state,
            action: PayloadAction<string | undefined>,
        ) => {
            state.selectedListId = action.payload;
        },
        clearDetail: (state) => {
            state.detail = null;
            state.selectedListId = undefined;
            state.detailRequestId = undefined;
            state.detailError = undefined;
        },
        resetLists: (state) => {
            state.lists = [];
            state.listsPropertyId = undefined;
            state.listsError = undefined;
        },
        clearManageError: (state) => {
            state.manageError = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomLists.pending, (state, action) => {
                state.isListsLoading = true;
                state.listsError = undefined;
                resetListsOnPropertyChange(state, action.meta.arg.propertyId);
            })
            .addCase(fetchCustomLists.fulfilled, (state, action) => {
                if (state.listsPropertyId !== action.meta.arg.propertyId) {
                    return;
                }
                state.isListsLoading = false;
                state.lists = action.payload;
                state.listsPropertyId = action.meta.arg.propertyId;
            })
            .addCase(fetchCustomLists.rejected, (state, action) => {
                if (state.listsPropertyId !== action.meta.arg.propertyId) {
                    return;
                }
                state.isListsLoading = false;
                state.listsError = action.payload;
            })
            .addCase(fetchCustomListDetail.pending, (state, action) => {
                if (!updatesDetail(action.meta.arg.updateDetail)) {
                    return;
                }
                state.isDetailLoading = true;
                state.detailError = undefined;
                state.selectedListId = action.meta.arg.listId;
                state.detailRequestId = action.meta.requestId;
                if (state.detail?.id !== action.meta.arg.listId) {
                    state.detail = null;
                }
            })
            .addCase(fetchCustomListDetail.fulfilled, (state, action) => {
                if (
                    !updatesDetail(action.meta.arg.updateDetail) ||
                    action.meta.requestId !== state.detailRequestId
                ) {
                    return;
                }
                state.isDetailLoading = false;
                state.detailError = undefined;
                state.detail = action.payload;
                state.selectedListId = action.payload.id;
                upsertListFromDetail(state, action.payload);
            })
            .addCase(fetchCustomListDetail.rejected, (state, action) => {
                if (
                    !updatesDetail(action.meta.arg.updateDetail) ||
                    action.meta.requestId !== state.detailRequestId
                ) {
                    return;
                }
                state.isDetailLoading = false;
                state.detailError = action.payload;
            })
            .addCase(createCustomList.pending, (state) => {
                state.isManaging = true;
                state.manageError = undefined;
            })
            .addCase(createCustomList.fulfilled, (state, action) => {
                state.isManaging = false;
                state.detail = action.payload;
                state.selectedListId = action.payload.id;
                upsertListFromDetail(state, action.payload);
            })
            .addCase(createCustomList.rejected, (state, action) => {
                state.isManaging = false;
                state.manageError = action.payload;
            })
            .addCase(renameCustomList.pending, (state) => {
                state.isManaging = true;
                state.manageError = undefined;
            })
            .addCase(renameCustomList.fulfilled, (state, action) => {
                state.isManaging = false;
                state.detail = action.payload;
                upsertListFromDetail(state, action.payload);
            })
            .addCase(renameCustomList.rejected, (state, action) => {
                state.isManaging = false;
                state.manageError = action.payload;
            })
            .addCase(deleteCustomList.pending, (state) => {
                state.isManaging = true;
                state.manageError = undefined;
            })
            .addCase(deleteCustomList.fulfilled, (state, action) => {
                state.isManaging = false;
                state.lists = state.lists.filter(
                    (list) => list.id !== action.payload,
                );
                if (state.selectedListId === action.payload) {
                    state.detail = null;
                    state.selectedListId = undefined;
                }
            })
            .addCase(deleteCustomList.rejected, (state, action) => {
                state.isManaging = false;
                state.manageError = action.payload;
            })
            .addCase(addCustomListItems.pending, (state) => {
                state.isManaging = true;
                state.manageError = undefined;
            })
            .addCase(addCustomListItems.fulfilled, (state, action) => {
                state.isManaging = false;
                state.detail = action.payload;
                upsertListFromDetail(state, action.payload);
            })
            .addCase(addCustomListItems.rejected, (state, action) => {
                state.isManaging = false;
                state.manageError = action.payload;
            })
            .addCase(removeCustomListItems.pending, (state) => {
                state.isManaging = true;
                state.manageError = undefined;
            })
            .addCase(removeCustomListItems.fulfilled, (state, action) => {
                state.isManaging = false;
                state.detail = action.payload;
                upsertListFromDetail(state, action.payload);
            })
            .addCase(removeCustomListItems.rejected, (state, action) => {
                state.isManaging = false;
                state.manageError = action.payload;
            })
            .addCase(updateCustomListItem.fulfilled, (state, action) => {
                if (!state.detail?.items) {
                    return;
                }
                const index = state.detail.items.findIndex(
                    (item) => item.id === action.payload.id,
                );
                if (index >= 0) {
                    state.detail.items[index] = {
                        ...state.detail.items[index],
                        ...action.payload,
                    };
                }
            });
    },
});

export const { actions: customListActions } = customListSlice;
export const { reducer: customListReducer } = customListSlice;
